import dayjs from "dayjs";

import Section from './Section';
import { useTranslation } from "react-i18next";
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';
import { useEffect, useId, useState } from 'react';
import { useAdministrationAPI } from '../AdministrationAPI';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';

import { userAPI } from '../UserAPI';

import { Invoice, InvoiceStatus, Project } from "@the-real-insight/tri-model";
import { ColumnDef } from "@tanstack/react-table";
import { FileCode, FileText } from "lucide-react";

import { useModalContext } from "./ModalProvider";
import { PDFInvoice, downloadInvoice, downloadFile } from '../billing/invoice';
import { Document, Page } from '@react-pdf/renderer';
import { pdfStyles } from '../pdf/PDF';

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface Properties {
    project: Project,
    projectChanged: (project: Project) => void,
}

interface PlanOption {
    key: any;
    text: string;
}

export default function InvoicePanel(properties: Properties) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError } = useModalContext() as any;
    const { getInvoiceForecast, getInvoices } = useAdministrationAPI();
    const fieldId = useId();
    const [customer, setCustomer] = useState() as any;
    const [order, setOrder] = useState() as any;
    const [currentBillingPeriodFrom, setCurrentBillingPeriodFrom] = useState(dayjs().startOf('M').toDate()) as any;
    const [currentBillingPeriodTo, setCurrentBillingPeriodTo] = useState(dayjs().endOf('M').toDate()) as any;
    const [currentInvoices, setCurrentInvoices] = useState() as any;
    const [currentInvoicePDF, _setCurrentInvoicePDF] = useState() as any;
    const currencyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [_orders, _setOrders] = useState([]) as any;
    const [invoices, setInvoices] = useState([]) as any;
    const [plans, _setPlans] = useState() as any;
    const [portalAndAppUsagePlan, setPortalAndAppUsagePlan] = useState() as any;
    const [privateDataIngestionPlan, setPrivateDataIngestionPlan] = useState() as any;

    const portalAndAppUsageItems = (): PlanOption[] => plans && plans.portalAndAppUsage ? plans.portalAndAppUsage.map((plan: any) => {
        return {
            key: plan.id,
            text: plan.name
        };
    }) : [];

    const privateDataIngestionItems = (): PlanOption[] => plans && plans.privateDataIngestion ? [{ key: null, text: t('global.none') }, ...plans.privateDataIngestion.map((plan: any) => {
        return {
            key: plan.id,
            text: plan.name
        };
    })] : [];

    const planSelectionComplete = (): boolean => {
        return portalAndAppUsagePlan !== undefined;
    };

    useEffect(() => {
        if (!authentication.customer || !properties.project) {
            return;
        }

        const call = async () => {
            const newCustomer = await userAPI.getCustomer(authentication.customer);

            newCustomer.address = newCustomer.address || {};

            setCustomer(newCustomer);

            // Current service orders

            const newOrder = await userAPI.fullOrderForProject(authentication.customer, properties.project._id as any);

            console.log('Order >>>', newOrder);

            if (!newOrder) {
                throw new Error('No order for project');
            }

            const periodFromDate = newOrder.startOfBillingPeriod();
            const periodToDate = newOrder.endOfBillingPeriod();

            console.log('Period from >>>', periodFromDate);
            console.log('Period to >>>', periodToDate);

            setOrder(newOrder);

            console.log('Project >>>', properties.project);
            console.log('Service Orders >>>', newOrder);

            setCurrentBillingPeriodFrom(periodFromDate);
            setCurrentBillingPeriodTo(periodToDate);

            const invoiceForecast = properties.project ? await userAPI.getInvoiceForecastForProject(authentication.customer, properties.project._id as any,
                periodFromDate, periodToDate) : await getInvoiceForecast(authentication.customer,
                    periodFromDate, periodToDate);

            console.log('Invoice forecast >>>', invoiceForecast);

            setCurrentInvoices(invoiceForecast);

            // if (customer && invoiceForecast) {
            //     setCurrentInvoicePDF(<PDFInvoice invoice={invoiceForecast} customer={customer}></PDFInvoice>);
            // }

            const invoicesResponse = await getInvoices(authentication.customer, new Date('2024-05-01T00:00:00'), new Date());
            const newInvoices = (invoicesResponse.inbound || [])
                .sort((a: Invoice, b: Invoice) => b.periodFromDate.getTime() - a.periodFromDate.getTime());

            console.log('Invoices >>>', newInvoices);

            setInvoices(newInvoices);
        };

        void call().catch((error: any) => {
            pushError(error);
        });
    }, [authentication.customer, getInvoiceForecast, getInvoices, properties.project, pushError]);

    const togglePlanDialogOpen = () => {
        setPlanDialogOpen(!planDialogOpen);
    }

    const downloadInvoicePDF = async (invoice: any) => {
        const doc = (
            <Document>
                <Page size="A4" style={pdfStyles.page}>
                    <PDFInvoice invoice={invoice} customer={customer} />
                </Page>
            </Document>
        );
        const blob = await pdf(doc).toBlob();
        downloadFile(blob, `Rechnung ${invoice.id}`);
    };

    /** PDF- und E-Rechnungs-Download, wie in der Tabelle und im Vorschaublock. */
    const invoiceDownloadButtons = (invoice: any) => (
        <div className="flex">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('billingPanel.downloadInvoiceForecastPDF')}
                        onClick={() => downloadInvoicePDF(invoice)}
                    >
                        <FileText className="h-4 w-4" aria-hidden />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{t('billingPanel.downloadInvoiceForecastPDF')}</TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('billingPanel.downloadInvoiceForecastERechnung')}
                        onClick={() => downloadInvoice(invoice)}
                    >
                        <FileCode className="h-4 w-4" aria-hidden />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>{t('billingPanel.downloadInvoiceForecastERechnung')}</TooltipContent>
            </Tooltip>
        </div>
    );

    const columns: ColumnDef<Invoice>[] = [{
        id: 'status',
        header: () => t('billingPanel.paymentStatusColumn'),
        cell: ({ row }) => (
            <div className={(row.original.status === InvoiceStatus.paid ? 'text-success' : 'text-destructive') + ' font-bold'}>
                {row.original.status === InvoiceStatus.paid ? 'Bezahlt' : 'Offen'}
            </div>
        ),
    }, {
        id: 'creationDate',
        header: () => t('billingPanel.invoiceDateColumn'),
        cell: ({ row }) => (
            <span className="text-sm">{dayjs(row.original.creationDate).format(t('global.dateShort'))}</span>
        ),
    }, {
        id: 'period',
        header: () => t('billingPanel.invoicePeriodColumn'),
        cell: ({ row }) => (
            <span className="text-sm">{dayjs(row.original.periodFromDate).format(t('global.dateShort'))} - {dayjs(new Date(row.original.periodToDate)).format(t('global.dateShort'))}</span>
        ),
    }, {
        id: 'amount',
        header: () => <div className="text-right">{t('billingPanel.amountColumn')}</div>,
        cell: ({ row }) => (
            <div className="text-right text-sm">{currencyFormat.format(row.original.subtotal())}</div>
        ),
    }, {
        id: 'download',
        header: () => t('billingPanel.downloadColumn'),
        cell: ({ row }) => invoiceDownloadButtons(row.original),
    },
    ];

    return <TooltipProvider><div>
        <div className="flex flex-wrap gap-6">
            <div className="flex min-w-0 flex-[2] basis-[520px] flex-col gap-6">
                <Section title={`Aktueller Abrechnungszeitraum ${dayjs(currentBillingPeriodFrom).format(t('global.dateShort'))} - ${dayjs(currentBillingPeriodTo).format(t('global.dateShort'))}`}>
                    {currentInvoices
                        ?
                        <>
                            {currentInvoices.map((invoice: Invoice) => (
                                <div key={invoice.id}>
                                    <div className="flex justify-end">
                                        {invoiceDownloadButtons(invoice)}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-bold">Bisheriger Betrag</span>
                                        <span className="text-3xl">{currencyFormat.format(invoice.grandTotal(0.19))}</span>
                                    </div>
                                    <div className="mt-6 flex">
                                        {invoices && invoices.length > 0
                                            ?
                                            <div className="min-w-0 flex-1">
                                                <div>
                                                    <span className="text-sm font-bold">Letzte Rechnung bis</span>
                                                </div>
                                                <div>
                                                    <span className="text-sm">{dayjs(invoice.periodToDate).format(t('global.dateShort'))}</span>
                                                </div>
                                            </div>
                                            :
                                            <></>
                                        }
                                    </div>
                                    <div className="mt-6 flex justify-end">
                                        {currentInvoicePDF ?
                                            <PDFDownloadLink document={currentInvoicePDF} fileName="Abrechnung Juli 2024.pdf">
                                                {/* <>{({ blob, url, loading, error }: any) =>
                                                    loading
                                                        ?
                                                        'Loading document...'
                                                        :
                                                        <PrimaryButton iconProps={{ iconName: 'Download' }}>
                                                            Rechnungsvorschau
                                                        </PrimaryButton>
                                                }</> */}
                                            </PDFDownloadLink>
                                            :
                                            <></>
                                        }
                                    </div>
                                </div>
                            ))}
                        </>
                        :
                        <></>}
                </Section>
                <Section title={t('billingPanel.invoices')}>
                    <div className="flex justify-end">
                        <Button type="button" variant="ghost" onClick={async () => {
                            console.log('Creating missing imnvoices');
                            // @ts-ignore
                            await userAPI.createMissingInvoicesForProject(authentication.customer, properties.project._id);
                        }}>
                            {t('billingPanel.createInvoice')}
                        </Button>
                    </div>
                    <div className="overflow-x-auto">
                        <DataTable
                            columns={columns}
                            data={invoices}
                            emptyMessage={t('settingsPanel.projectsPanel.noObjectsDefined')}
                        />
                    </div>
                </Section>
            </div>
            <div className="flex min-w-0 flex-1 basis-72 flex-col gap-6">
                <Section title="Abrechnungsmodalitäten">
                    <div className="flex max-w-sm flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-invoiceType`}>Rechnungsart</Label>
                        <Select
                            value={properties.project.invoiceType}
                            onValueChange={(invoiceType) => { properties.projectChanged(new Project({ ...properties.project, invoiceType })) }}
                        >
                            <SelectTrigger id={`${fieldId}-invoiceType`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="direct">Direkt an Endkunden</SelectItem>
                                <SelectItem value="indirect">Indirekt über Partner</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="mt-5 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Checkbox id={`${fieldId}-mailCustomerContact`} onCheckedChange={() => { }} />
                            <Label htmlFor={`${fieldId}-mailCustomerContact`} className="font-normal">E-Mail an Kundenkontakt</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox id={`${fieldId}-mailPartnerBilling`} onCheckedChange={() => { }} />
                            <Label htmlFor={`${fieldId}-mailPartnerBilling`} className="font-normal">E-Mail an Nutzende des Partners in Rolle Abrechnung</Label>
                        </div>
                    </div>
                </Section>
                <Section title="Aktuelle Lizenzen">
                    {order && properties.project
                        ?
                        <>
                            <ul className="list-disc pl-5 text-sm">
                                {order.orderItems.map((orderItem: any) =>
                                    <li key={orderItem.service.name + orderItem.plan.description}>
                                        <span className="font-bold">{orderItem.service.name}:</span> {orderItem.plan.description}
                                    </li>
                                )}
                            </ul>
                        </>
                        :
                        <>
                            <ul className="list-disc pl-5 text-sm">
                                <li>
                                    <div className="font-bold">Installation Digitaler Feuerlöscher</div> 120,00€
                                </li>
                                <li>
                                    <div className="font-bold">Wartung Digitaler Feuerlöscher</div> 24,00€/Quartal
                                </li>
                            </ul>
                        </>
                    }
                    <div className="mt-6 flex justify-end">
                        <Button type="button" onClick={() => { togglePlanDialogOpen() }}>Ändern</Button>
                    </div>
                </Section>
            </div>
        </div>
    </div>
        <Dialog
            open={planDialogOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setPlanDialogOpen(false);
                }
            }}
        >
            <DialogContent
                className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.billingPanel.licenses')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-portalAndAppUsage`}>Nutzung von Portal und App</Label>
                        <Select
                            value={portalAndAppUsagePlan ? String(portalAndAppUsagePlan.id) : undefined}
                            onValueChange={(value) => {
                                if (plans.portalAndAppUsage) {
                                    setPortalAndAppUsagePlan(plans.portalAndAppUsage.find((plan: any) => String(plan.id) === value));
                                }
                            }}
                        >
                            <SelectTrigger id={`${fieldId}-portalAndAppUsage`}>
                                <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {portalAndAppUsageItems().map((option) => (
                                    <SelectItem key={String(option.key)} value={String(option.key)}>{option.text}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="min-h-[50px] text-xs">
                            {portalAndAppUsagePlan ? portalAndAppUsagePlan.description : ''}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${fieldId}-privateDataIngestion`}>Management privater Daten</Label>
                        <Select
                            value={privateDataIngestionPlan ? String(privateDataIngestionPlan.id) : undefined}
                            onValueChange={(value) => {
                                if (plans.privateDataIngestion) {
                                    setPrivateDataIngestionPlan(plans.privateDataIngestion.find((plan: any) => String(plan.id) === value));
                                }
                            }}
                        >
                            <SelectTrigger id={`${fieldId}-privateDataIngestion`}>
                                <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {privateDataIngestionItems().map((option) => (
                                    <SelectItem key={String(option.key)} value={String(option.key)}>{option.text}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="min-h-[50px] text-xs">
                            {privateDataIngestionPlan ? privateDataIngestionPlan.description : ''}
                        </div>
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" type="button" onClick={togglePlanDialogOpen}>{t('global.cancel')}</Button>
                    <Button type="button" disabled={!planSelectionComplete()} onClick={async () => {
                    }}>{t('global.change')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog></TooltipProvider>;
}
